import { DEBUG_OVERRIDE, NOT_LAMBDA } from '../../lambda'
import { ComparisonType, ExpressionFunctionType, FilterGroup, FilterPartType, LogicalOperatorType, NullValueFilterPartType } from '@iotv/iotv-v3-types'
import { NativeAttributeValue } from '@aws-sdk/util-dynamodb'

type AttributeMap = Record<string, NativeAttributeValue>
type ItemList = AttributeMap[]

const debug = DEBUG_OVERRIDE || (NOT_LAMBDA && false)

type PredicateToTestFnMap = { [k in ComparisonType | ExpressionFunctionType]: (key: string, value: any) => (item: AttributeMap) => boolean }
const predicateToTestFn: PredicateToTestFnMap = {
  [ComparisonType.EQUALS]: (key: string, value: any) => (item: AttributeMap) => item[key] === value,
  [ComparisonType.NOT_EQUALS]: (key: string, value: any) => (item: AttributeMap) => item[key] !== value,
  [ExpressionFunctionType.CONTAINS]: (key: string, value: any) => (item: AttributeMap) => item[key]?.toString().includes(value),
  [ExpressionFunctionType.NOT_CONTAINS]: (key: string, value: any) => (item: AttributeMap) => !item[key]?.toString().includes(value),
  [ExpressionFunctionType.EXISTS]: (key: string, value: any) => (item: AttributeMap) => !!item[key],
  [ExpressionFunctionType.NOT_EXISTS]: (key: string, value: any) => (item: AttributeMap) => !item[key],
  [ExpressionFunctionType.BEGINS_WITH]: (key: string, value: any) => (item: AttributeMap) => item[key]?.toString().startsWith(value),
  [ExpressionFunctionType.NOT_BEGINS_WITH]: (key: string, value: any) => (item: AttributeMap) => !item[key]?.toString().startsWith(value),
  [ComparisonType.LESS_THAN]: (key: string, value: any) => (item: AttributeMap) => item[key] < value,
  [ComparisonType.LESS_EQUAL]: (key: string, value: any) => (item: AttributeMap) => item[key] <= value,
  [ComparisonType.GREATER_THAN]: (key: string, value: any) => (item: AttributeMap) => item[key] > value,
  [ComparisonType.GREATER_EQUAL]: (key: string, value: any) => (item: AttributeMap) => item[key] >= value,
}

type NodeListSubFilter = (item: AttributeMap) => boolean
const getFilterFnFromFilterPartType = (filterPartType: FilterPartType | NullValueFilterPartType) => {
  const { key, predicate, value } = filterPartType
  const filterFn: NodeListSubFilter = (item) => {
    let pass = false
    const filterFn = predicateToTestFn[filterPartType.predicate]
    /** To support dot notation (!) instead pass key accessed value from key splitting logic. As this changes predicateToTestFn, perhaps that logic goes there, or change all the function just to accept value from dot noted atts  */
    pass = filterFn(key, value)(item)
    return pass
  }

  return filterFn
}



type NodeListFilter = (item: AttributeMap) => boolean

type GetFilterFnsFromFilterGroupsInput = {
  filterGroups: FilterGroup[]
}

// note this looks like it handles top level AND between nodeFilterGroups but not OR

/** returns array of filters each of which is has an array of subfilters which are AND or OR'd depending on the setOperation of the filterGroup */
export const getFilterFnsFromFilterGroups = ({ filterGroups }: GetFilterFnsFromFilterGroupsInput): NodeListFilter[] => {
  const filterFns: NodeListFilter[] = filterGroups.map((filterGroup) => {
    const subFilters = filterGroup.filters.map((filter) => getFilterFnFromFilterPartType(filter))
    debug && console.log('Subfilters', subFilters)
    let filterFn: NodeListFilter = (item) => true
    // const setOperationFn = filterGroup.setOperation === LogicalOperatorType.AND ? subFilters.every : subFilters.some
    if (filterGroup.setOperation === LogicalOperatorType.AND) {
      filterFn = (item: AttributeMap) => subFilters.every((subFilter) => subFilter(item))
    } else {
      filterFn = (item: AttributeMap) => subFilters.some((subFilter) => subFilter(item))
    }

    return filterFn
  })

  return filterFns
}



export type FilterNodeListRequest = { 
  nodeFilterGroups: FilterGroup[]
  groupSetOperation?: LogicalOperatorType // default LogicalOperatorType.OR
  nodes: ItemList 
  }

/** applies the array of filter functions derived from the nodeFilterGroup definitions */
export const filterNodeList = ({ nodeFilterGroups, groupSetOperation = LogicalOperatorType.AND, nodes }: FilterNodeListRequest): ItemList => {


  debug && console.log('filtering', { nodes, nodeFilterGroups })
  const filterFns = getFilterFnsFromFilterGroups({ filterGroups: nodeFilterGroups })
  let filterNodes: ItemList = [] 
  if ( groupSetOperation === LogicalOperatorType.AND ) {
    // apply each filter the the filtered result of last filter (AND)
    filterNodes = filterFns.reduce((a, c) => a.filter(c), nodes)
  } else {
    const noFilters = filterFns.length === 0
    filterNodes  = nodes.reduce( ( a: ItemList, c: AttributeMap ) => {
      // 2025 06 pass every node through every filter and if passes some add to result (OR), potentially
     if ( noFilters || filterFns.some( ( fn ) => fn( c )) ) {
      a.push( c )
     }
     return a
    }, [] )
  }

  return filterNodes
}

export type ApplyNodeFilterRequest = { nodeFilterGroups: FilterGroup[]; nodes: ItemList; edges: ItemList }
export const applyNodeFilter = ({ nodeFilterGroups, nodes, edges }: ApplyNodeFilterRequest) => {
  const filteredNodes: ItemList = filterNodeList({ nodeFilterGroups, nodes })
  const nodeFilteredEdges: ItemList = []
  /** only return edges which match filtered nodes */
  edges.forEach((edge) => {
    const sourceOrTargetNodePresent = filteredNodes.find((node) => node.sk === edge.sk || node.pk === edge.pk)
    if (sourceOrTargetNodePresent) {
      nodeFilteredEdges.push(edge)
    }
  })

  return { filteredNodes, nodeFilteredEdges }
}
